import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, computed, inject, input, OnInit, signal, viewChild } from '@angular/core';

import { formatCurrency } from '@angular/common';
import { ColDef, GridOptions } from 'ag-grid-community';
import { sumBy } from 'lodash';
import { SubscriptionService } from 'src/app/service/subscription.service';
import { defaultTableDefOptions } from '../../helpers';
import { NotifyService } from '../../service/notify.service';
import { BranchImpl } from '../../types';
import { StripePurchaseUiActionsComponent } from './stripe-purchase-ui-actions/stripe-purchase-ui-actions.component';
import { StripePurchaseUiCartActionsComponent } from './stripe-purchase-ui-cart-actions/stripe-purchase-ui-cart-actions.component';

@Component({
  selector: 'app-stripe-purchase-ui',
  templateUrl: './stripe-purchase-ui.component.html',
  styleUrls: ['./stripe-purchase-ui.component.scss'],
  standalone: false,
})
export class StripePurchaseUiComponent implements OnInit {
  private subscriptionService = inject(SubscriptionService);
  private notifyService = inject(NotifyService);

  readonly viewport = viewChild(CdkVirtualScrollViewport);
  readonly branch = input<BranchImpl>(undefined);
  productsColumns = ['name', 'price', 'action'];
  cartColumns = ['name', 'price', 'quantity', 'subtotal'];
  products = signal<any[]>([]);
  cart = signal<any[]>([]);

  public totalCartValue = computed(() => sumBy(this.cart(), (item) => this.getPrice(item) * item.quantity));

  public isLoading = signal<boolean>(false);

  public readonly itemTableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly itemColumnDefs: ColDef[] = [
    {
      field: 'name',
      tooltipField: 'name',
      headerName: 'Product Name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Price',
      valueFormatter: (params) => {
        const intervalText = params.data.default_price.type === 'recurring' ? this.getPriceInterval(params.data.default_price) : '';
        return formatCurrency(params.data.default_price.unit_amount / 100, 'en-US', '$') + ' ' + intervalText;
      },
    },
    {
      maxWidth: 80,
      cellRenderer: StripePurchaseUiActionsComponent,
      cellRendererParams: {
        addCallback: (item) => {
          this.addToCart(item, 1);
        },
      },
    },
  ];

  public readonly cartTableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly cartColumnDefs: ColDef[] = [
    {
      field: 'name',
      headerName: 'Bundle Name',
      tooltipField: 'name',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Price',
      valueFormatter: (params) => {
        return formatCurrency(params.data.default_price.unit_amount / 100, 'en-US', '$');
      },
    },
    {
      headerName: 'Quantity',
      maxWidth: 200,
      cellRenderer: StripePurchaseUiCartActionsComponent,
      cellRendererParams: {
        plusCallback: (item) => {
          this.addToCart(item, 1);
        },
        minusCallback: (item) => {
          this.addToCart(item, -1);
        },
      },
    },
    {
      headerName: 'Subtotal',
      valueFormatter: (params) => {
        return formatCurrency((params.data.quantity * params.data.default_price.unit_amount) / 100, 'en-US', '$');
      },
    },
  ];

  ngOnInit(): void {
    this.isLoading.set(true);

    this.subscriptionService.getProducts().subscribe({
      next: (products) => {
        this.products.set(products);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  getPrice(element) {
    return element.default_price.unit_amount / 100;
  }

  getPriceInterval(price) {
    const intervalCount = price.recurring.interval_count;
    const interval = price.recurring.interval;
    if (intervalCount === 1) {
      return '/' + interval;
    }
    return `Every ${intervalCount} ${interval}s`;
  }

  addToCart(product: any, quantity: number) {
    const cartItem = this.cart().find((c) => c.id === product.id);
    if (cartItem) {
      this.cart.update((cart) => {
        const item = cart.find((c) => c.id === product.id);
        if (item) {
          item.quantity += quantity;
          if (item.quantity === 0) {
            return cart.filter((c) => c.id !== product.id);
          }
        }
        return [...cart];
      });
    } else {
      this.cart.set([...this.cart(), { ...product, quantity }]);
    }
  }

  checkout() {
    this.subscriptionService.purchaseProducts(this.branch().id, this.cart()).subscribe({
      next: () => {
        this.notifyService.notify('Purchase successful');
        this.cart.set([]);
      },
      error: () => {
        this.notifyService.notify('Purchase failed');
      },
    });
  }
}
